import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  Copy,
  Download,
  FileText,
  Pencil,
  Pin,
  RotateCw,
  SmilePlus,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import type { Agent, Attachment, Message } from '../../types';
import { cn, ui } from '../../utils';
import { formatShortcut, formatFileSize, getFileIcon } from '../../utils';
import AgentAvatar from '../Common/AgentAvatar';
import { EmojiReactionPicker, ReactionPills } from './EmojiReactionPicker';
import MarkdownRenderer from './MarkdownRenderer';
import MessageMeta from './MessageMeta';

interface MessageBubbleProps {
  message: Message;
  isUser: boolean;
  isLast: boolean;
  agent?: Agent | null;
  isNew?: boolean;
  className?: string;
  timeReference: number;
  onReact?: (messageId: string, reaction: string) => void;
  onPin?: (messageId: string) => void;
  onRetry?: (messageId: string) => void;
  onDismiss?: (messageId: string) => void;
  onRegenerate?: (messageId: string) => void;
  onEdit?: (messageId: string, content: string) => Promise<void> | void;
  onRate?: (messageId: string, rating: 'up' | 'down') => Promise<void> | void;
}

function getMessageRating(message: Message): 'up' | 'down' | null {
  const rating = message.metadata && typeof message.metadata === 'object'
    ? message.metadata.rating
    : null;

  return rating === 'up' || rating === 'down' ? rating : null;
}

export function MessageBubble({
  message,
  isUser,
  agent,
  className,
  timeReference,
  onReact,
  onPin,
  onRetry,
  onDismiss,
  onRegenerate,
  onEdit,
  onRate,
}: MessageBubbleProps) {
  const isStreaming = message.status === 'streaming';
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftContent, setDraftContent] = useState(message.content);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const reactions = message.reactions ?? { counts: {}, userReactions: [], reactors: {} };
  const currentRating = getMessageRating(message);
  const streamingState = typeof message.metadata?.streamingState === 'string' ? message.metadata.streamingState : 'streaming';
  const streamingLabel = streamingState === 'starting'
    ? null
    : streamingState === 'recovering'
      ? 'Recovering stream...'
      : null;
  const sendShortcut = formatShortcut(['Mod', 'Enter']);

  useEffect(() => {
    if (!isEditing) {
      setDraftContent(message.content);
    }
  }, [isEditing, message.content]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(draftContent.length, draftContent.length);
  }, [draftContent.length, isEditing]);

  const handleCopy = useCallback(async () => {
    const plainText = message.content
      .replace(/```[\s\S]*?```/g, (block) => block.replace(/^```[\w-]*\n?/, '').replace(/```$/, '').trim())
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
      .replace(/^>\s?/gm, '')
      .replace(/^[\-*+]\s+/gm, '• ')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/[*_~`]/g, '')
      .trim();

    try {
      await navigator.clipboard.writeText(plainText || message.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [message.content]);

  const handleDownload = async (attachment: Attachment) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveEdit = useCallback(async () => {
    const nextContent = draftContent.trim();
    if (!onEdit) {
      setIsEditing(false);
      return;
    }

    if (!nextContent || nextContent === message.content.trim()) {
      setDraftContent(message.content);
      setIsEditing(false);
      return;
    }

    setIsSavingEdit(true);
    try {
      await onEdit(message.id, nextContent);
      setIsEditing(false);
    } finally {
      setIsSavingEdit(false);
    }
  }, [draftContent, message.content, message.id, onEdit]);

  const handleCancelEdit = useCallback(() => {
    setDraftContent(message.content);
    setIsEditing(false);
    setIsSavingEdit(false);
  }, [message.content]);

  const shouldShowActionToolbar = !isStreaming && !isEditing && (isHovered || showReactionPicker);
  const shouldShowRatingActions = !isUser && Boolean(onRate) && !isStreaming && (shouldShowActionToolbar || Boolean(currentRating));

  const bubble = (
    <div
      className={cn(
        'w-fit max-w-[min(100%,44rem)] min-w-[80px] break-words rounded-[1.4rem] px-4 py-3 shadow-sm transition-colors',
        isUser
          ? 'rounded-br-lg bg-blue-600 text-white shadow-blue-950/20'
          : 'rounded-bl-lg border border-border bg-card text-foreground shadow-black/10',
        isUser && message.status === 'failed' && 'border border-red-500/40 bg-red-500/10 text-red-50',
      )}
    >
      {message.attachments?.filter((attachment) => attachment.contentType.startsWith('image/')).length ? (
        <div className="mb-2 space-y-2">
          {message.attachments
            ?.filter((attachment) => attachment.contentType.startsWith('image/'))
            .map((attachment) => (
              <FileAttachment
                key={attachment.id}
                attachment={attachment}
                isUser={isUser}
                onDownload={handleDownload}
              />
            ))}
        </div>
      ) : null}

      <div className={cn('max-w-none text-sm leading-6', isUser ? 'text-white' : 'text-foreground')}>
        {isUser ? (
          isEditing ? (
            <div className="space-y-3">
              <textarea
                ref={textareaRef}
                value={draftContent}
                onChange={(event) => setDraftContent(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    handleCancelEdit();
                    return;
                  }

                  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    void handleSaveEdit();
                  }
                }}
                rows={Math.min(Math.max(draftContent.split('\n').length, 3), 8)}
                disabled={isSavingEdit}
                className="w-full resize-y rounded-xl border border-white/20 bg-white/10 px-3 py-2.5 text-sm text-white outline-none placeholder:text-white/60 transition-colors duration-150 focus-visible:border-white/40"
              />
              <div className="flex items-center justify-end gap-2 text-xs text-white/80">
                <span className="mr-auto">Save with {sendShortcut}, cancel with Esc</span>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSavingEdit}
                  className="inline-flex min-h-[36px] items-center gap-1 rounded-lg border border-white/20 px-3 py-1.5 transition-colors duration-150 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                  title="Cancel editing (Esc)"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveEdit()}
                  disabled={isSavingEdit || !draftContent.trim()}
                  className="inline-flex min-h-[36px] items-center gap-1 rounded-lg bg-white/15 px-3 py-1.5 font-medium transition-colors duration-150 hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                  title={`Save edit (${sendShortcut})`}
                >
                  {isSavingEdit ? <span className="h-3.5 w-3.5 animate-spin rounded-full border border-white/40 border-t-white" /> : <Check className="h-3.5 w-3.5" />}
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="whitespace-pre-wrap">{message.content}</div>
          )
        ) : (
          <div className={cn(isStreaming && 'streaming-content')}>
            <MarkdownRenderer content={message.content} />
            {isStreaming ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                {streamingLabel ? <span>{streamingLabel}</span> : null}
                <span className="flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: '0ms' }} />
                  <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: '150ms' }} />
                  <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {message.attachments?.filter((attachment) => !attachment.contentType.startsWith('image/')).length ? (
        <div className="mt-3 space-y-2">
          {message.attachments
            ?.filter((attachment) => !attachment.contentType.startsWith('image/'))
            .map((attachment) => (
              <FileAttachment
                key={attachment.id}
                attachment={attachment}
                isUser={isUser}
                onDownload={handleDownload}
              />
            ))}
        </div>
      ) : null}
    </div>
  );

  const actionToolbar = shouldShowActionToolbar ? (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-border/70 bg-card/95 p-1 shadow-sm shadow-black/10 backdrop-blur-sm">
      {isUser && onEdit ? (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className={cn(ui.button.icon, 'h-9 w-9 rounded-full p-0 hover:bg-background')}
          title="Edit message"
          aria-label="Edit message"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      ) : null}
      {onReact ? (
        <button
          type="button"
          onClick={() => setShowReactionPicker((open) => !open)}
          className={cn(
            ui.button.icon,
            'h-9 w-9 rounded-full p-0 hover:bg-background',
            showReactionPicker && 'text-blue-300',
          )}
          title="React"
          aria-label="React to message"
        >
          <SmilePlus className="h-3.5 w-3.5" />
        </button>
      ) : null}
      {onPin ? (
        <button
          type="button"
          onClick={() => onPin(message.id)}
          className={cn(
            ui.button.icon,
            'h-9 w-9 rounded-full p-0 hover:bg-background',
            message.isPinned && 'text-blue-300',
          )}
          title={message.isPinned ? 'Unpin message' : 'Pin message'}
          aria-label={message.isPinned ? 'Unpin message' : 'Pin message'}
        >
          <Pin className="h-3.5 w-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => void handleCopy()}
        className={cn(
          ui.button.icon,
          'h-9 w-9 rounded-full p-0 hover:bg-background',
          copied && 'text-emerald-400',
        )}
        title={copied ? 'Copied!' : 'Copy message'}
        aria-label={copied ? 'Message copied' : 'Copy message'}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
      {!isUser && onRegenerate ? (
        <button
          type="button"
          onClick={() => onRegenerate(message.id)}
          className={cn(ui.button.icon, 'h-9 w-9 rounded-full p-0 hover:bg-background')}
          title="Regenerate response"
          aria-label="Regenerate response"
        >
          <RotateCw className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  ) : null;

  const bubbleColumn = (
    <div className={cn('relative flex min-w-0 max-w-full flex-col', isUser ? 'items-end' : 'items-start')}>
      {bubble}

      {Object.keys(reactions.counts).some((key) => (reactions.counts[key] || 0) > 0) ? (
        <ReactionPills
          counts={reactions.counts}
          userReactions={reactions.userReactions}
          reactors={reactions.reactors}
          onToggle={(emoji) => onReact?.(message.id, emoji)}
        />
      ) : null}

      {actionToolbar || shouldShowRatingActions ? (
        <div className="mt-2 flex w-full justify-end">
          <div className="flex items-center gap-2">
            {shouldShowRatingActions ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => void onRate?.(message.id, 'up')}
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-150',
                    currentRating === 'up'
                      ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
                      : 'border-border bg-card text-muted-foreground hover:border-blue-500/30 hover:text-foreground',
                  )}
                  title="Helpful response"
                  aria-label="Helpful response"
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => void onRate?.(message.id, 'down')}
                  className={cn(
                    'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-150',
                    currentRating === 'down'
                      ? 'border-red-400/40 bg-red-500/15 text-red-200'
                      : 'border-border bg-card text-muted-foreground hover:border-blue-500/30 hover:text-foreground',
                  )}
                  title="Needs work"
                  aria-label="Needs work"
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
            {actionToolbar}
          </div>
        </div>
      ) : null}

      {!isUser && !isStreaming && showReactionPicker ? (
        <div className="mt-1 w-full">
          <div className="flex justify-end">
            <EmojiReactionPicker
              onSelect={(emoji) => {
                onReact?.(message.id, emoji);
                setShowReactionPicker(false);
              }}
              userReactions={reactions.userReactions}
            />
          </div>
        </div>
      ) : null}

      <MessageMeta
        message={message}
        isUser={isUser}
        timeReference={timeReference}
        align={isUser ? 'right' : 'left'}
        onRetry={onRetry}
        onDismiss={onDismiss}
      />
    </div>
  );

  return (
    <div
      className={cn('group/msg flex w-full', isUser ? 'justify-end' : 'justify-start', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isUser ? (
        bubbleColumn
      ) : (
        <div className="flex max-w-full items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <AgentAvatar agent={{ name: agent?.name || 'Assistant', avatar: agent?.avatar }} size="sm" />
          </div>
          {bubbleColumn}
        </div>
      )}
    </div>
  );
}

interface FileAttachmentProps {
  attachment: Attachment;
  isUser: boolean;
  onDownload: (attachment: Attachment) => void;
}

function FileAttachment({ attachment, isUser, onDownload }: FileAttachmentProps) {
  const fileIcon = getFileIcon(attachment.filename);
  const isImage = attachment.contentType.startsWith('image/');

  if (isImage) {
    return (
      <div className={cn('relative max-w-sm overflow-hidden rounded-xl', isUser ? 'bg-white/10' : 'border border-border bg-card')}>
        <img
          src={attachment.url}
          alt={attachment.filename}
          className="max-h-64 w-full cursor-pointer object-cover"
          onClick={() => onDownload(attachment)}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
          <div className="flex items-center justify-between">
            <p className="mr-2 flex-1 truncate text-xs font-medium text-white">{attachment.filename}</p>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDownload(attachment);
              }}
              className="rounded-md p-1 text-white transition-colors duration-150 hover:bg-white/10 hover:text-white/80"
              title="Download"
              aria-label={`Download ${attachment.filename}`}
            >
              <Download className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-xl p-3 transition-colors duration-150 hover:opacity-100',
        isUser ? 'bg-white/10 hover:bg-white/20' : 'border border-border bg-card hover:bg-background',
      )}
      onClick={() => onDownload(attachment)}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background font-semibold text-xs text-foreground">
        {fileIcon.length <= 3 ? fileIcon : <FileText className="h-5 w-5" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-sm font-medium', isUser ? 'text-white' : 'text-foreground-bright')}>
          {attachment.filename}
        </p>
        <p className={cn('text-xs', isUser ? 'text-white/70' : 'text-muted-foreground')}>
          {formatFileSize(attachment.size)}
        </p>
      </div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDownload(attachment);
        }}
        className={cn('rounded-lg p-2 transition-colors duration-150', isUser ? 'text-white hover:bg-white/20' : 'text-muted-foreground hover:bg-background hover:text-foreground')}
        title="Download"
        aria-label={`Download ${attachment.filename}`}
      >
        <Download className="h-4 w-4" />
      </button>
    </div>
  );
}

const MemoizedMessageBubble = React.memo(MessageBubble, (prevProps, nextProps) => (
  prevProps.message === nextProps.message
  && prevProps.isUser === nextProps.isUser
  && prevProps.isLast === nextProps.isLast
  && prevProps.agent === nextProps.agent
  && prevProps.isNew === nextProps.isNew
  && prevProps.className === nextProps.className
  && prevProps.timeReference === nextProps.timeReference
  && prevProps.onReact === nextProps.onReact
  && prevProps.onPin === nextProps.onPin
  && prevProps.onRetry === nextProps.onRetry
  && prevProps.onDismiss === nextProps.onDismiss
  && prevProps.onRegenerate === nextProps.onRegenerate
  && prevProps.onEdit === nextProps.onEdit
  && prevProps.onRate === nextProps.onRate
));

export default MemoizedMessageBubble;
